import { z } from "zod";
import * as auth from "../auth";
import * as models from "./models";
import {
  PhonesValidationErrorResponse,
  ValidationErrorResponse,
} from "./models";
import { ChannelMapToFull } from "../channelUtil";
import { formatISO } from "date-fns";

// NOTE: DO NOT CHANGE unless you are certain what you are doing
export const BASE_URL = import.meta.env?.VITE_API_URL ?? "/playwright-tests";

const strings = {
  dataError:
    "An unexpected error has occurred. Please retry or refresh the page.",
};

// custom errors
export class HTTPError extends Error {
  message: string;
  status: number;
  responseText?: string;
  validation?: ValidationErrorResponse;
  constructor({
    message,
    status,
    responseText,
    validation,
  }: {
    message: string;
    status: number;
    responseText?: string;
    validation?: ValidationErrorResponse;
  }) {
    super(message);
    this.message = message;
    this.status = status;
    this.responseText = responseText;
    this.validation = validation;
  }
}

export class PhonesHTTPError extends HTTPError {
  validation?: PhonesValidationErrorResponse;
  constructor({
    message,
    status,
    responseText,
    validation,
  }: {
    message: string;
    status: number;
    responseText?: string;
    validation?: PhonesValidationErrorResponse;
  }) {
    super({ message, status, responseText });
    this.validation = validation;
  }
}

export const genericErrorKey = "general";

export const MakePaymentNegAmStatusCode = 402 as const;
export const PaymentOverrideOrDynamicErrorStatusCode = 406 as const;
export const PaymentScriptsStatusCode = 412 as const;
export const SourceAccountDynamicErrorStatusCode = 406 as const;

export type NetworkDisplayError = {
  status: number;
  message: string;
};

// converts a fetching related error (e.g. HTTPError / ZodError / etc) into a presentable string
export function getDisplayError(error: unknown): NetworkDisplayError {
  if (!(error instanceof HTTPError)) {
    // Programmer Error
    return {
      status: 500,
      message: strings.dataError,
    };
  }
  const errors = error.validation?.errors[genericErrorKey];
  if (errors) {
    return {
      status: error.status,
      message: errors.join(" "),
    };
  }
  // Programmer Error
  return {
    status: 500,
    message: strings.dataError,
  };
}

export function getDisplayMessages(
  resp: models.MessagesResponse,
): NetworkDisplayError {
  return {
    status: 200,
    message: resp.messages.join(" "),
  };
}

async function parseError(
  response: Response,
  endpointName: string,
): Promise<never> {
  const responseText = await response.text();
  try {
    const validationError = models.validationErrorResponse.parse(
      JSON.parse(responseText),
    );
    throw new HTTPError({
      message: `Endpoint '${endpointName}' failed with status code ${response.status}`,
      status: response.status,
      validation: validationError,
    });
  } catch (err) {
    if (err instanceof SyntaxError) {
      // This is a text-based error, like "Invalid ID parameter"
      throw new HTTPError({
        message: `Endpoint '${endpointName}' failed with status code ${response.status} and message '${responseText}'`,
        status: response.status,
        responseText,
      });
    }

    // Could be a ZodError, like validationError was JSON but schema validation failed
    throw err;
  }
}

async function parseNetworkResponse<InputT, OutputT>(
  response: Response,
  intoModel: z.ZodType<OutputT, z.ZodTypeDef, InputT>,
  endpointName: string,
  errorModel?: z.ZodType,
) {
  if (response.ok) {
    if (intoModel === models.unknownModel) {
      // doesn't matter really
      return intoModel.parse(await response.text());
    }
    return intoModel.parse(await response.json());
  }

  const responseText = await response.text();
  if (errorModel === models.phonesValidationErrorResponse) {
    try {
      const phonesValidationError = models.phonesValidationErrorResponse.parse(
        JSON.parse(responseText),
      );
      throw new PhonesHTTPError({
        message: `Endpoint '${endpointName}' failed with status code ${response.status}`,
        status: response.status,
        validation: phonesValidationError,
      });
    } catch (err) {
      // catch block catches all errors thrown in try block above. check and rethrow if PhonesHTTPError
      if (err instanceof PhonesHTTPError) {
        throw err;
      }
    }
  }

  // default HTTPError or SyntaxError
  try {
    const validationError = models.validationErrorResponse.parse(
      JSON.parse(responseText),
    );
    throw new HTTPError({
      message: `Endpoint '${endpointName}' failed with status code ${response.status}`,
      status: response.status,
      validation: validationError,
    });
  } catch (err) {
    if (err instanceof SyntaxError) {
      // This is a text-based error, like "Invalid ID parameter"
      throw new HTTPError({
        message: `Endpoint '${endpointName}' failed with status code ${response.status} and message '${responseText}'`,
        status: response.status,
        responseText,
      });
    }

    // Could be a ZodError, like validationError was JSON but schema validation failed
    throw err;
  }
}

async function baseFetch({
  url,
  method,
  headers,
  body,
  keepalive,
}: Pick<RequestInit, "body" | "headers" | "method" | "keepalive"> & {
  url: string;
}): Promise<Response> {
  const token = await auth.getAuthorizationToken();
  const BASE_HEADERS: typeof headers = {
    Authorization: `Bearer ${token}`,
  };

  return await fetch(`${BASE_URL}/${url}`, {
    method,
    body,
    keepalive,
    headers: {
      ...BASE_HEADERS,
      ...headers,
    },
  });
}

// endpoints
export const getAgentDetails = async () => {
  const response = await baseFetch({
    url: `my-account`,
  });
  return parseNetworkResponse(
    response,
    models.getAgentDetailsResponse,
    "getAgentDetails",
  );
};

export const getAccount = async (dwbuid: string) => {
  const response = await baseFetch({
    url: `account/${dwbuid}`,
  });
  return parseNetworkResponse(
    response,
    models.getAccountResponse,
    "getAccount",
  );
};

export const getAccountDetails = async (dwbuid: string) => {
  const response = await baseFetch({
    url: `account/${dwbuid}/details`,
  });
  return parseNetworkResponse(
    response,
    models.getAccountDetailsResponse,
    "getAccountDetails",
  );
};

export const updateUsername = async ({
  partyId,
  newUsername,
}: models.UpdateUsernameRequest) => {
  const response = await baseFetch({
    url: `account/${partyId}/username`,
    method: "POST",
    body: JSON.stringify({ newUsername }),
  });
  return parseNetworkResponse(response, models.unknownModel, "updateUsername");
};

export const sendTestPushNotification = async ({
  partyId,
  referenceId = "",
  channel,
}: models.SendTestPushNotificationRequest) => {
  const searchParams = new URLSearchParams({
    referenceId,
    channel,
  });
  const response = await baseFetch({
    url: `account/${partyId}/push-notification?${searchParams}`,
    method: "POST",
  });
  return parseNetworkResponse(
    response,
    models.unknownModel,
    "sendTestPushNotification",
  );
};

export const getAccountLockStatus = async (dwbuid: string) => {
  const response = await baseFetch({
    url: `account/${dwbuid}/lock`,
  });
  return parseNetworkResponse(
    response,
    models.getAccountLockStatusResponse,
    "getAccountLockStatus",
  );
};

export const updateAccountLockStatus = async ({
  dwbuid,
}: models.UpdateAccountLockStatusRequest) => {
  const response = await baseFetch({
    url: `account/${dwbuid}/lock`,
    method: "POST",
  });
  return parseNetworkResponse(
    response,
    models.unknownModel,
    "updateAccountLockStatus",
  );
};

export const getPaymentHistory = async (dwbuid: string) => {
  const response = await baseFetch({
    url: `account/${dwbuid}/payments/history`,
  });
  return parseNetworkResponse(
    response,
    models.getPaymentHistoryResponse,
    "getPaymentHistory",
  );
};

export const getAutopayEnrollment = async ({
  partyId,
  dwbuid,
}: models.GetAutopayEnrollmentRequest) => {
  const searchParams = new URLSearchParams({
    dwbuid,
  });
  const response = await baseFetch({
    url: `account/${partyId}/autopay-enrollment?${searchParams}`,
  });
  return parseNetworkResponse(
    response,
    models.getAutopayEnrollmentResponse,
    "getAutopayEnrollment",
  );
};

export const getSecureMailList = async (
  req: models.GetSecureMailListRequest,
) => {
  const searchParams = new URLSearchParams({
    page: req.page.toString(),
    size: req.size.toString(),
  });
  const response = await baseFetch({
    url: `secure-mail/classification/${req.classificationId}?${searchParams}`,
  });
  return parseNetworkResponse(
    response,
    models.getSecureMailListResponse,
    "getSecureMailList",
  );
};

export const patchNextSecureMailId = async ({
  classificationId,
  mailId,
}: models.PatchNextSecureMailIdRequest) => {
  const response = await baseFetch({
    url: `secure-mail/classification/${classificationId}`,
    method: "PATCH",
    body: JSON.stringify({ mailId }),
  });
  return parseNetworkResponse(
    response,
    models.patchNextSecureMailIdResponse,
    "patchNextSecureMailId",
  );
};

export const patchSecureMailClassification = async ({
  secureMailId,
  classificationId,
}: models.PatchSecureMailClassificationRequest) => {
  const response = await baseFetch({
    url: `secure-mail/${secureMailId}/classification`,
    method: "PATCH",
    body: JSON.stringify({ classificationId: classificationId }),
  });
  return parseNetworkResponse(
    response,
    models.unknownModel,
    "patchSecureMailClassification",
  );
};

export const getClassifications = async () => {
  const response = await baseFetch({
    url: `classifications`,
    headers: {
      "Content-Type": "application/json",
    },
  });
  return parseNetworkResponse(
    response,
    models.getClassificationsResponse,
    "getClassifications",
  );
};

export const getClassificationCount = async () => {
  const response = await baseFetch({
    url: `classification-count`,
    headers: {
      "Content-Type": "application/json",
    },
  });
  return parseNetworkResponse(
    response,
    models.getClassificationCountResponse,
    "getClassificationCount",
  );
};

export const getCardmemberMailList = async ({
  partyId,
  ...query
}: models.GetCardmemberMailListRequest) => {
  const response = await baseFetch({
    url:
      `secure-mail/card-member/${partyId}?` +
      new URLSearchParams({
        page: query.page.toString(),
        size: query.size.toString(),
        start: query.start ? query.start : "",
        end: query.end ? query.end : "",
      }),
    headers: {
      "Content-Type": "application/json",
    },
  });
  return parseNetworkResponse(
    response,
    models.getCardmemberMailListResponse,
    "getCardmemberMailList",
  );
};

export const getSecureMailThread = async ({
  secureMailId,
}: models.GetSecureMailThreadRequest) => {
  const response = await baseFetch({
    url: `secure-mail/${secureMailId}`,
    headers: {
      "Content-Type": "application/json",
    },
  });
  return parseNetworkResponse(
    response,
    models.getSecureMailThreadResponse,
    "getSecureMailThread",
  );
};

export const addToThread = async (req: models.AddToThreadRequest) => {
  const data = new FormData();
  if (req.messageBody) {
    data.append("body", req.messageBody);
  }
  if (req.file) {
    data.append("file", req.file, req.file.name);
  }

  const response = await baseFetch({
    url: `secure-mail/${req.secureMailId}`,
    method: "POST",
    body: data,
  });
  return parseNetworkResponse(response, models.unknownModel, "addToThread");
};

export const composeSecureMail = async ({
  partyId,
  subject,
  classificationId,
  status,
  body,
  file,
}: models.ComposeSecureMailRequest) => {
  const data = new FormData();
  data.append("subject", subject);
  data.append("classificationId", classificationId);
  data.append("status", status);
  if (body) {
    data.append("body", body);
  }
  if (file) {
    data.append("file", file, file.name);
  }

  const response = await baseFetch({
    url: `secure-mail/${partyId}/compose`,
    method: "POST",
    body: data,
  });
  return parseNetworkResponse(
    response,
    models.composeSecureMailResponse,
    "composeSecureMail",
  );
};

export const updateSecureMailStatus = async ({
  secureMailId,
  ...reqBody
}: models.UpdateSecureMailStatusRequest) => {
  const response = await baseFetch({
    url: `secure-mail/${secureMailId}/status`,
    method: "PATCH",
    body: JSON.stringify(reqBody),
  });
  return parseNetworkResponse(
    response,
    models.unknownModel,
    "updateSecureMailStatus",
  );
};

export const viewMailAttachment = async (
  reqBody: models.ViewMailAttachmentRequest,
) => {
  const { mailId, messageId, attachmentId } = reqBody;

  const response = await baseFetch({
    url: `secure-mail/${mailId}/message/${messageId}/attachment/${attachmentId}`,
    headers: {
      "Content-Type": "application/json",
    },
  });
  return parseNetworkResponse(
    response,
    models.viewMailAttachmentResponse,
    "viewMailAttachment",
  );
};

export const enrollAutopay = async ({
  partyId,
  ...reqBody
}: models.EnrollAutopayRequest) => {
  const response = await baseFetch({
    url: `account/${partyId}/autopay-enrollment`,
    method: "POST",
    body: JSON.stringify(reqBody),
  });
  return parseNetworkResponse(response, models.unknownModel, "enrollAutopay");
};

export const removeAutopayEnrollment = async ({
  partyId,
  ...reqBody
}: models.SkipRemoveAutopayRequest) => {
  const response = await baseFetch({
    url: `account/${partyId}/autopay-enrollment`,
    method: "DELETE",
    body: JSON.stringify(reqBody),
  });
  return parseNetworkResponse(
    response,
    models.unknownModel,
    "removeAutopayEnrollment",
  );
};

export const skipAutopay = async ({
  partyId,
  ...reqBody
}: models.SkipRemoveAutopayRequest) => {
  const response = await baseFetch({
    url: `account/${partyId}/autopay-enrollment/skip`,
    method: "POST",
    body: JSON.stringify(reqBody),
  });
  return parseNetworkResponse(response, models.unknownModel, "skipAutopay");
};

export const getPaymentTransactions = async (
  req: models.GetPaymentTransactionsRequest,
) => {
  const response = await baseFetch({
    url: `account/${req.dwbuid}/payments/transactions`,
  });
  return parseNetworkResponse(
    response,
    models.getPaymentTransactionsResponse,
    "getPaymentTransactions",
  );
};

export const getPaymentMethods = async (
  req: models.GetPaymentMethodsRequest,
) => {
  const response = await baseFetch({
    url: `account/${req.partyId}/payment-methods`,
  });
  return parseNetworkResponse(
    response,
    models.getPaymentMethodsResponse,
    "getPaymentMethods",
  );
};

export const deleteSourceAccount = async ({
  partyId,
  sourceAccountId,
}: {
  partyId: string;
  sourceAccountId: string;
}) => {
  const response = await baseFetch({
    url: `account/${partyId}/payment-methods/${sourceAccountId}`,
    method: "DELETE",
  });

  return parseNetworkResponse(
    response,
    models.unknownModel,
    "deleteSourceAccount",
  );
};

export const addBankSourceAccount = async ({
  partyId,
  ...body
}: models.AddBankSourceAccountRequest) => {
  const response = await baseFetch({
    url: `account/${partyId}/payment-methods/bank-account`,
    method: "POST",
    body: JSON.stringify(body),
  });

  // NOTE: B2C Source Accounts do not need to support dynamic errors

  return parseNetworkResponse(
    response,
    models.addBankSourceAccountResponse,
    "addBankSourceAccount",
  );
};

export const editBankSourceAccount = async ({
  partyId,
  sourceAccountId,
  ...body
}: models.EditBankSourceAccountRequest) => {
  const response = await baseFetch({
    url: `account/${partyId}/payment-methods/bank-account/${sourceAccountId}`,
    method: "PATCH",
    body: JSON.stringify(body),
  });

  // NOTE: B2C Source Accounts do not need to support dynamic errors

  return parseNetworkResponse(
    response,
    models.editBankSourceAccountResponse,
    "editBankSourceAccount",
  );
};

export const addCardSourceAccount = async ({
  partyId,
  ...body
}: models.AddCardSourceAccountRequest) => {
  const response = await baseFetch({
    url: `account/${partyId}/payment-methods/card-account`,
    method: "POST",
    body: JSON.stringify(body),
  });

  if (response.ok) {
    return models.addCardSourceAccountResponse.parse(await response.json());
  } else if (response.status === SourceAccountDynamicErrorStatusCode) {
    throw {
      ...models.dynamicErrorResponse.parse(await response.json()),
      expectedError: true,
    };
  } else {
    return parseError(response, "addCardSourceAccount");
  }
};

export const editCardSourceAccount = async ({
  partyId,
  sourceAccountId,
  ...body
}: models.EditCardSourceAccountRequest) => {
  const response = await baseFetch({
    url: `account/${partyId}/payment-methods/card-account/${sourceAccountId}`,
    method: "PATCH",
    body: JSON.stringify(body),
  });

  if (response.ok) {
    return models.editCardSourceAccountResponse.parse(await response.json());
  } else if (response.status === SourceAccountDynamicErrorStatusCode) {
    throw {
      ...models.dynamicErrorResponse.parse(await response.json()),
      expectedError: true,
    };
  } else {
    return parseError(response, "editCardSourceAccount");
  }
};

export const makeOneTimePayment = async ({
  partyId,
  ...reqBody
}: models.MakeOneTimePaymentRequest) => {
  const body = {
    ...reqBody,
    paymentAmount: "custom",
  };
  const response = await baseFetch({
    url: `account/${partyId}/payments/onetime`,
    method: "POST",
    body: JSON.stringify(body),
  });

  if (response.ok) {
    return models.makeOneTimePaymentResponse.parse(await response.json());
  } else if (response.status === MakePaymentNegAmStatusCode) {
    throw {
      ...models.makePaymentNegAmResponse.parse(await response.json()),
      expectedError: true,
    };
  } else if (response.status === PaymentScriptsStatusCode) {
    throw {
      ...models.paymentScriptsResponse.parse(await response.json()),
      expectedError: true,
    };
  } else if (response.status === PaymentOverrideOrDynamicErrorStatusCode) {
    const json = await response.json();
    if (json.type === models.DYNAMIC_ERROR) {
      throw { ...models.dynamicErrorResponse.parse(json), expectedError: true };
    } else {
      throw {
        ...models.paymentOverridesResponse.parse(json),
        expectedError: true,
      };
    }
  } else {
    return parseError(response, "makeOneTimePayment");
  }
};

export const makeSeriesPayment = async ({
  partyId,
  ...body
}: models.MakeSeriesPaymentRequest) => {
  const response = await baseFetch({
    url: `account/${partyId}/payments/series`,
    method: "POST",
    body: JSON.stringify(body),
  });

  if (response.ok) {
    return models.makeSeriesPaymentResponse.parse(await response.json());
  } else if (response.status === MakePaymentNegAmStatusCode) {
    throw {
      ...models.makePaymentNegAmResponse.parse(await response.json()),
      expectedError: true,
    };
  } else if (response.status === PaymentScriptsStatusCode) {
    throw {
      ...models.paymentScriptsResponse.parse(await response.json()),
      expectedError: true,
    };
  } else if (response.status === PaymentOverrideOrDynamicErrorStatusCode) {
    const json = await response.json();
    if (json.type === models.DYNAMIC_ERROR) {
      throw { ...models.dynamicErrorResponse.parse(json), expectedError: true };
    } else {
      throw {
        ...models.paymentOverridesResponse.parse(json),
        expectedError: true,
      };
    }
  } else {
    return parseError(response, "makeSeriesPayment");
  }
};

export const getPaymentSeriesSchedule = async (
  req: models.GetPaymentSeriesScheduleRequest,
) => {
  const response = await baseFetch({
    url: "payments/series-schedule",
    method: "POST",
    body: JSON.stringify(req),
  });
  return parseNetworkResponse(
    response,
    models.getPaymentSeriesScheduleResponse,
    "getPaymentSeriesSchedule",
  );
};

export const getPartyRestrictions = async (partyId: string) => {
  const response = await baseFetch({
    url: `account/${partyId}/restrictions`,
  });
  return parseNetworkResponse(
    response,
    models.getPartyRestrictionsResponse,
    "getPartyRestrictions",
  );
};

export const getBankNames = async (abaNumber: string) => {
  const response = await baseFetch({
    url: `bank-names/${abaNumber}`,
  });
  return parseNetworkResponse(
    response,
    models.getBankNamesResponse,
    "getBankNames",
  );
};

export const addBankRestriction = async ({
  partyId,
  ...body
}: models.AddRestrictionRequest) => {
  const response = await baseFetch({
    url: `account/${partyId}/restrictions/bank`,
    method: "POST",
    body: JSON.stringify(body),
  });
  return parseNetworkResponse(
    response,
    models.unknownModel,
    "addBankRestriction",
  );
};

export const addCardRestriction = async ({
  partyId,
  ...body
}: models.AddRestrictionRequest) => {
  const response = await baseFetch({
    url: `account/${partyId}/restrictions/card`,
    method: "POST",
    body: JSON.stringify(body),
  });
  return parseNetworkResponse(
    response,
    models.unknownModel,
    "addCardRestriction",
  );
};

export const cancelPayment = async ({
  partyId,
  paymentId,
}: {
  partyId: string;
  paymentId: string;
}) => {
  const response = await baseFetch({
    url: `account/${partyId}/payments/${paymentId}`,
    method: "DELETE",
  });
  return parseNetworkResponse(response, models.unknownModel, "cancelPayment");
};

export const editOneTimePayment = async ({
  partyId,
  paymentId,
  ...reqBody
}: models.EditOneTimePaymentRequest) => {
  const body = { ...reqBody, paymentAmount: "custom" };
  const response = await baseFetch({
    url: `account/${partyId}/payments/${paymentId}`,
    method: "PATCH",
    body: JSON.stringify(body),
  });

  if (response.ok) {
    return models.editOneTimePaymentResponse.parse(await response.json());
  } else if (response.status === PaymentScriptsStatusCode) {
    throw {
      ...models.paymentScriptsResponse.parse(await response.json()),
      expectedError: true,
    };
  } else if (response.status === PaymentOverrideOrDynamicErrorStatusCode) {
    const json = await response.json();
    if (json.type === models.DYNAMIC_ERROR) {
      throw { ...models.dynamicErrorResponse.parse(json), expectedError: true };
    } else {
      throw {
        ...models.paymentOverridesResponse.parse(json),
        expectedError: true,
      };
    }
  } else {
    return parseError(response, "editOneTimePayment");
  }
};

export const editSeriesPayment = async ({
  partyId,
  seriesId,
  ...reqBody
}: models.EditSeriesPaymentRequest) => {
  const response = await baseFetch({
    url: `account/${partyId}/payments/series/${seriesId}`,
    method: "PATCH",
    body: JSON.stringify(reqBody),
  });

  if (response.ok) {
    return models.unknownModel.parse(await response.text());
  } else if (response.status === PaymentScriptsStatusCode) {
    throw {
      ...models.paymentScriptsResponse.parse(await response.json()),
      expectedError: true,
    };
  } else if (response.status === PaymentOverrideOrDynamicErrorStatusCode) {
    const json = await response.json();
    if (json.type === models.DYNAMIC_ERROR) {
      throw { ...models.dynamicErrorResponse.parse(json), expectedError: true };
    } else {
      throw {
        ...models.paymentOverridesResponse.parse(json),
        expectedError: true,
      };
    }
  } else {
    return parseError(response, "editSeriesPayment");
  }
};

export const cancelSeriesPayment = async ({
  partyId,
  seriesId,
  ...reqBody
}: models.CancelSeriesPaymentRequest) => {
  const response = await baseFetch({
    url: `account/${partyId}/payments/series/${seriesId}`,
    method: "DELETE",
    body: JSON.stringify(reqBody),
  });

  return parseNetworkResponse(
    response,
    models.unknownModel,
    "cancelSeriesPayment",
  );
};

export const getPermissionToDiscuss = async ({
  partyId,
  includeHistory,
  includeInactive,
}: models.GetPermissionToDiscussRequest) => {
  const searchParams = new URLSearchParams({
    history: includeHistory.toString(),
    includeInactive: includeInactive.toString(),
  });
  const response = await baseFetch({
    url: `account/${partyId}/permission-to-discuss?${searchParams}`,
  });
  return parseNetworkResponse(
    response,
    models.getPermissionToDiscussResponse,
    "getPermissionToDiscuss",
  );
};

export const addPermissionToDicuss = async ({
  partyId,
  ...rest
}: models.AddPermissionToDiscussRequest) => {
  const response = await baseFetch({
    url: `account/${partyId}/permission-to-discuss`,
    method: "POST",
    body: JSON.stringify(rest),
  });
  return parseNetworkResponse(
    response,
    models.unknownModel,
    "addPermissionToDiscuss",
  );
};

export const editPermissionToDicuss = async ({
  partyId,
  permissionId,
  ...rest
}: models.EditPermissionToDiscussRequest) => {
  const response = await baseFetch({
    url: `account/${partyId}/permission-to-discuss/${permissionId}`,
    method: "PATCH",
    body: JSON.stringify(rest),
  });

  return parseNetworkResponse(
    response,
    models.unknownModel,
    "editPermissionToDiscuss",
  );
};

export const verifyPermissionToDiscuss = async ({
  partyId,
  ...rest
}: models.VerifyPermissionToDiscussRequest): Promise<unknown> => {
  const response = await baseFetch({
    url: `account/${partyId}/permission-to-discuss/verify`,
    method: "POST",
    body: JSON.stringify(rest),
  });

  return parseNetworkResponse(
    response,
    models.unknownModel,
    "verifyPermissionToDiscuss",
  );
};

export const removePermissionToDiscuss = async ({
  partyId,
  permissionId,
  ...rest
}: models.RemovePermissionToDiscussRequest): Promise<unknown> => {
  const response = await baseFetch({
    url: `account/${partyId}/permission-to-discuss/${permissionId}`,
    method: "DELETE",
    body: JSON.stringify(rest),
  });

  return parseNetworkResponse(
    response,
    models.unknownModel,
    "deletePermissionToDiscuss",
  );
};

export const getCycleToDateTransactions = async (dwbuid: string) => {
  const response = await baseFetch({
    url: `account/${dwbuid}/cycle-transactions`,
  });
  return parseNetworkResponse(
    response,
    models.getCycleToDateTransactionsResponse,
    "getCycleToDateTransactions",
  );
};

export const getStatementHistory = async (dwbuid: string) => {
  const response = await baseFetch({
    url: `account/${dwbuid}/statement-history`,
  });
  return parseNetworkResponse(
    response,
    models.getStatementHistoryResponse,
    "getStatementHistory",
  );
};

export const getLostStolenOptions = async (dwbuid: string) => {
  const response = await baseFetch({
    url: `account/${dwbuid}/lost-stolen-options`,
  });
  return parseNetworkResponse(
    response,
    models.lostStolenOptionsResponse,
    "getLostStolenOptions",
  );
};

export const submitLostStolen = async ({
  dwbuid,
  ...rest
}: models.SubmitLostStolenRequest) => {
  const response = await baseFetch({
    url: `account/${dwbuid}/lost-stolen`,
    method: "POST",
    body: JSON.stringify(rest),
  });
  return parseNetworkResponse(
    response,
    models.unknownModel,
    "submitLostStolen",
  );
};

export const getCreditLineIncreaseEligibility = async ({
  dwbuid,
  referenceId = "",
  channel,
}: models.GetCreditLineIncreaseEligibilityRequest) => {
  const searchParams = new URLSearchParams({
    referenceId,
    channel,
  });
  const response = await baseFetch({
    url: `account/${dwbuid}/credit-line-increase?${searchParams}`,
  });
  return parseNetworkResponse(
    response,
    models.getCreditLineIncreaseEligibilityResponse,
    "getCreditLineIncreaseEligibility",
  );
};

export const submitCreditLineIncrease = async ({
  dwbuid,
  ...rest
}: models.SubmitCreditLineIncreaseRequest) => {
  const response = await baseFetch({
    url: `account/${dwbuid}/credit-line-increase`,
    method: "POST",
    body: JSON.stringify(rest),
  });
  return parseNetworkResponse(
    response,
    models.submitCreditLineIncreaseResponse,
    "submitCreditLineIncrease",
  );
};

export const getCallInfo = async ({
  dwbuid,
  channel,
  callId,
  phoneNumber,
}: models.GetCallInfoRequest) => {
  const response = await baseFetch({
    url: `account/${dwbuid}/call-info`,
    method: "POST",
    body: JSON.stringify({
      callId,
      channel: ChannelMapToFull[channel],
      phoneNumber,
    }),
  });

  return parseNetworkResponse(
    response,
    models.getCallInfoResponse,
    "getCallInfo",
  );
};

export const getTerms = async (dwbuid: string) => {
  const response = await baseFetch({
    url: `account/${dwbuid}/terms`,
    method: "GET",
  });

  return parseNetworkResponse(
    response,
    models.getTermsResponse,
    "getTermsResponse",
  );
};

export const getPhoneOptions = async () => {
  const response = await baseFetch({
    url: `phones/options`,
  });

  return parseNetworkResponse(
    response,
    models.getPhoneOptionsResponse,
    "getPhoneOptions",
  );
};

export const getPhones = async (dwbuid: string) => {
  const response = await baseFetch({
    url: `account/${dwbuid}/phones`,
  });

  return parseNetworkResponse(response, models.getPhonesResponse, "getPhones");
};

export const verifyPhones = async ({
  partyId,
  phoneIDs,
  referenceId,
}: models.VerifyPhonesRequest) => {
  const response = await baseFetch({
    url: `account/${partyId}/phones/verify`,
    method: "POST",
    body: JSON.stringify({ phoneIDs, referenceId }),
  });

  return parseNetworkResponse(response, models.unknownModel, "verifyPhones");
};

export const addPhone = async ({
  partyId,
  ...rest
}: models.AddPhoneRequest) => {
  const response = await baseFetch({
    url: `account/${partyId}/phones`,
    method: "POST",
    body: JSON.stringify(rest),
  });

  return parseNetworkResponse(
    response,
    models.addEditPhoneResponse,
    "addPhone",
    models.phonesValidationErrorResponse,
  );
};

export const editPhone = async ({
  partyId,
  phoneId,
  ...rest
}: models.EditPhonesRequest) => {
  const response = await baseFetch({
    url: `account/${partyId}/phones/${phoneId}`,
    method: "PATCH",
    body: JSON.stringify(rest),
  });

  return parseNetworkResponse(
    response,
    models.addEditPhoneResponse,
    "editPhone",
    models.phonesValidationErrorResponse,
  );
};

export const getInactivationChangeMessages = async (
  request: models.GetInactivationChangeMessagesRequest,
) => {
  const response = await baseFetch({
    url: "phones/inactivation-change-messages",
    method: "POST",
    body: JSON.stringify(request),
  });

  return parseNetworkResponse(
    response,
    models.getInactivationChangeMessagesResponse,
    "getInactivationChangeMessages",
  );
};

export const accountSearch = async (req: models.AccountSearchRequest) => {
  const response = await baseFetch({
    url: "account/search",
    method: "POST",
    body: JSON.stringify(req),
  });

  return parseNetworkResponse(
    response,
    models.accountSearchResponse,
    "accountSearch",
  );
};

export const accountSearchByThreeOfFour = async (
  req: models.AccountSearchByThreeOfFourRequest,
) => {
  const response = await baseFetch({
    url: "account/search-three-of-four",
    method: "POST",
    body: JSON.stringify(req),
  });

  return parseNetworkResponse(
    response,
    models.accountSearchResponse,
    "accountSearchByThreeOfFour",
  );
};

export const getCitiesByZipcode = async (zipcode: string) => {
  const searchParams = new URLSearchParams({
    zipcode,
  });
  const response = await baseFetch({
    url: `cities?${searchParams}`,
    method: "GET",
  });

  return parseNetworkResponse(
    response,
    models.getCitiesByZipcodeResponse,
    "getCitiesByZipcode",
  );
};

export const getCustomerInfo = async ({
  dwbuid,
  referenceId = "",
}: models.GetCustomerInfoRequest) => {
  const searchParams = new URLSearchParams({
    referenceId: referenceId,
  });

  const response = await baseFetch({
    url: `account/${dwbuid}/customer-info?${searchParams}`,
    method: "GET",
  });

  return parseNetworkResponse(response, models.customerInfo, "getCustomerInfo");
};

export const updateCustomerInfo = async ({
  dwbuid,
  ...rest
}: models.UpdateCustomerInfoRequest) => {
  const response = await baseFetch({
    url: `account/${dwbuid}/customer-info`,
    method: "PATCH",
    body: JSON.stringify(rest),
  });

  return parseNetworkResponse(
    response,
    models.updateCustomerInfoResponse,
    "updateCustomerInfo",
  );
};

export const getChangedHistory = async ({
  dwbuid,
  referenceId = "",
  startDate = "",
  endDate = "",
}: models.GetChangedHistoryRequest) => {
  const searchParams = new URLSearchParams({
    referenceId,
    startDate,
    endDate,
  });

  const response = await baseFetch({
    url: `account/${dwbuid}/changed-history?${searchParams}`,
    method: "GET",
  });

  return parseNetworkResponse(
    response,
    models.getChangedHistoryResponse,
    "getChangedHistory",
  );
};

export const getVerifiedHistory = async ({
  dwbuid,
  referenceId = "",
  startDate = "",
  endDate = "",
}: models.GetVerifiedHistoryRequest) => {
  const searchParams = new URLSearchParams({
    referenceId,
    startDate,
    endDate,
  });

  const response = await baseFetch({
    url: `account/${dwbuid}/verified-history?${searchParams}`,
    method: "GET",
  });

  return parseNetworkResponse(
    response,
    models.getVerifiedHistoryResponse,
    "getVerifiedHistory",
  );
};

export const getStatements = async ({
  dwbuid,
  year = "",
}: models.GetStatementsRequest) => {
  const searchParams = new URLSearchParams({
    year,
  });

  const response = await baseFetch({
    url: `account/${dwbuid}/statements?${searchParams}`,
    method: "GET",
  });

  return parseNetworkResponse(response, models.statementsResp, "getStatements");
};

export const getStatementUrl = async (
  baseUrl: string,
  date: string,
): Promise<string> => {
  const token = await auth.getAuthorizationToken();

  const searchParms = new URLSearchParams({
    token: token,
    date: date,
  });

  return `${baseUrl}?${searchParms}`;
};

export const getPaperlessSettings = async ({
  dwbuid,
}: models.GetPaperlessSettingsRequest) => {
  const response = await baseFetch({
    url: `account/${dwbuid}/paperless-settings`,
    method: "GET",
  });

  return parseNetworkResponse(
    response,
    models.getPaperlessSettingsResponse,
    "getPaperlessSettings",
  );
};

export const updatePaperlessSettings = async ({
  dwbuid,
  partyId: _, // Needed for query invalidation, not request
  ...rest
}: models.PatchPaperlessSettingsRequest) => {
  const response = await baseFetch({
    url: `account/${dwbuid}/paperless-settings`,
    method: "PATCH",
    body: JSON.stringify(rest),
  });

  return parseNetworkResponse(
    response,
    models.unknownModel,
    "updatePaperlessSettings",
  );
};

export const getCardActivationInfo = async (
  dwbuid: string,
): Promise<models.GetCardActivationInfoResponse> => {
  const response = await baseFetch({
    url: `account/${dwbuid}/card-activation`,
    method: "GET",
  });

  return parseNetworkResponse(
    response,
    models.getCardActivationInfoResponse,
    "getCardActivationInfo",
  );
};

export const submitCardActivation = async ({
  dwbuid,
  ...rest
}: models.SubmitCardActivationRequest): Promise<models.SubmitCardActivationResponse> => {
  const response = await baseFetch({
    url: `account/${dwbuid}/card-activation`,
    method: "POST",
    body: JSON.stringify(rest),
  });

  return parseNetworkResponse(
    response,
    models.submitCardActivationResponse,
    "updateCustomerInfo",
  );
};

export const getTextBankingServices = async (
  dwbuid: string,
): Promise<models.GetTextBankingServicesResponse> => {
  const response = await baseFetch({
    url: `account/${dwbuid}/text-banking`,
  });
  return parseNetworkResponse(
    response,
    models.textBankingServices,
    "getTextBankingServices",
  );
};

export const stopFteuService = async ({
  dwbuid,
  phoneId,
  referenceId = "",
}: models.StopFteuServiceRequest) => {
  const searchParams = new URLSearchParams({
    referenceId,
  });
  const response = await baseFetch({
    url: `account/${dwbuid}/text-banking/${phoneId}?${searchParams}`,
    method: "PATCH",
  });
  return parseNetworkResponse(response, models.unknownModel, "stopFteuService");
};

export const stopFraudAlertService = async ({
  dwbuid,
  ...rest
}: models.StopFraudServiceRequest) => {
  const response = await baseFetch({
    url: `account/${dwbuid}/fraud-alert`,
    method: "PATCH",
    body: JSON.stringify(rest),
  });
  return parseNetworkResponse(
    response,
    models.unknownModel,
    "stopFrauAlertService",
  );
};

export const getResultOptions = async (channel: models.Channel) => {
  const response = await baseFetch({
    url: `result/options/${ChannelMapToFull[channel]}`,
    method: "GET",
  });

  return parseNetworkResponse(
    response,
    models.resultsDropdownsByChannel,
    "getResultOptions",
  );
};

export const updatePermCollQueue = async ({
  dwbuid,
  ...rest
}: models.UpdatePermCollQueueRequest) => {
  const response = await baseFetch({
    url: `account/${dwbuid}/result/perm-coll-queue`,
    method: "PATCH",
    body: JSON.stringify(rest),
  });

  return parseNetworkResponse(
    response,
    models.unknownModel,
    "updatePermCollQueue",
  );
};

export const submitResultCsr = async ({
  dwbuid,
  ...rest
}: models.ResultCsrRequest) => {
  const response = await baseFetch({
    url: `account/${dwbuid}/result/csr`,
    method: "POST",
    body: JSON.stringify(rest),
  });

  return parseNetworkResponse(
    response,
    models.messagesResponse,
    "submitResultCsr",
  );
};

export const submitResultNoPayment = async ({
  dwbuid,
  ...rest
}: models.ResultNoPaymentRequest) => {
  const response = await baseFetch({
    url: `account/${dwbuid}/result/no-payment`,
    method: "POST",
    body: JSON.stringify(rest),
  });

  return parseNetworkResponse(
    response,
    models.messagesResponse,
    "submitResultNoPayment",
  );
};

export const submitResultPayment = async ({
  dwbuid,
  ...rest
}: models.ResultPaymentRequest) => {
  const response = await baseFetch({
    url: `account/${dwbuid}/result/payment`,
    method: "POST",
    body: JSON.stringify(rest),
  });

  return parseNetworkResponse(
    response,
    models.messagesResponse,
    "submitResultPayment",
  );
};

export const submitResultResearchOnly = async ({
  dwbuid,
  ...rest
}: models.ResultResearchOnlyRequest) => {
  const response = await baseFetch({
    url: `account/${dwbuid}/result/research-only`,
    method: "POST",
    body: JSON.stringify(rest),
    keepalive: true,
  });

  return parseNetworkResponse(
    response,
    models.unknownModel,
    "submitResultResearchOnly",
  );
};

export const getNotesSearch = async ({
  dwbuid,
  ...rest
}: models.GetNotesSearchRequest): Promise<models.GetNotesSearchResponse> => {
  const response = await baseFetch({
    url: `account/${dwbuid}/notes`,
    method: "POST",
    body: JSON.stringify(rest),
  });

  return parseNetworkResponse(
    response,
    models.getNotesSearchResponse,
    "getNotesSearch",
  );
};

export const getNoteDetails = async (
  dwbuid: string,
  noteId: string,
): Promise<models.NoteDetail> => {
  const response = await baseFetch({
    url: `account/${dwbuid}/notes/${noteId}`,
    method: "GET",
  });

  return parseNetworkResponse(response, models.noteDetail, "getNoteDetails");
};

export const addNote = async ({
  dwbuid,
  ...rest
}: models.AddNoteRequest): Promise<models.MessagesResponse> => {
  const response = await baseFetch({
    url: `account/${dwbuid}/notes/new`,
    method: "POST",
    body: JSON.stringify(rest),
  });

  return parseNetworkResponse(response, models.messagesResponse, "addNote");
};

export const getListen = async (initialTime: Date | null) => {
  const searchParams = new URLSearchParams({
    initialTime: initialTime ? formatISO(initialTime) : "",
  });
  const response = await baseFetch({
    url: `listen?${searchParams}`,
    method: "GET",
  });

  return parseNetworkResponse(response, models.listenResponse, "getListen");
};

export const callEnded = async (req: models.CallEndedRequest) => {
  const response = await baseFetch({
    url: "customer-call-context/hangup",
    method: "POST",
    body: JSON.stringify(req),
  });

  return parseNetworkResponse(response, models.unknownModel, "callEnded");
};
